import type { FooterTextKey } from "@components/shared/Footer/footerConfig";
import type { NavNodeId } from "@components/shared/navigation/navConfig";
import type { LocaleId } from "@utils/localeConfig";

export interface ObelixKeyEntry {
  /** English fallback label handed to `t()` as its second argument. */
  label: string;
  /** ALH lookup key into the owning namespace. */
  obelixKey: string;
}

/**
 * Central registry of ALH/Obelix shared-string keys used by the ads client.
 *
 * Each entry pairs the ALH/i18next `obelixKey` (the lookup key into the
 * `ads-common` namespace) with the hardcoded `label` English fallback that
 * `t()` receives as its second argument. The label is what still renders
 * before ALH returns translations, or while a placeholder locale is in use.
 *
 * Add new entries here when introducing a new shared/chrome string in a
 * component, and add the matching key in
 * `client/src/locales/en/ads-common.json`.
 */
export const OBELIX_COMMON_KEYS = {
  logo: { label: "Netflix Ads", obelixKey: "logo-netflix-ads" },
  scrollDown: { label: "Scroll down", obelixKey: "scroll-down" },
  newsVisual: { label: "News visual", obelixKey: "news-visual" },
  heroOverlay: { label: "Overlay", obelixKey: "hero-overlay" },
  /**
   * Visible audience-section heading (QC-012): the German homepage shipped
   * the English "Monthly Active Viewers" because the heading was rendered
   * verbatim from CMS copy. Routing it through `t()` hands ownership to the
   * same ALH pipeline as the a11y chrome; the authored CMS title remains
   * the English fallback.
   */
  monthlyActiveViewers: {
    label: "Monthly Active Viewers",
    obelixKey: "audience-monthly-active-viewers",
  },
};

export const OBELIX_404_KEYS = {
  title: { label: "404", obelixKey: "404-title" },
  body: {
    label: "Red light! This page is not moving.",
    obelixKey: "404-body",
  },
  cta: { label: "Back To Home", obelixKey: "404-cta" },
};

/**
 * Shared chrome strings for the carousel variants ads renders
 * (quotation, slide, image). The `previous` / `next` pair is intentionally
 * shared so a single translated value works across every carousel; the
 * per-call-site `t()` label supplies the English fallback that names the
 * specific context (e.g. "Previous quotation").
 */
export const OBELIX_CAROUSEL_KEYS = {
  previous: { label: "Previous", obelixKey: "carousel-previous" },
  next: { label: "Next", obelixKey: "carousel-next" },
  controls: { label: "Slide controls", obelixKey: "carousel-controls" },
  titleFallback: { label: "Carousel", obelixKey: "carousel-title-fallback" },
  slideCount: {
    label: "Slide {current} of {total}",
    obelixKey: "carousel-slide-count",
  },
  /**
   * Play/pause control label (QC-012). Used as the `t()` fallback when a
   * carousel entry has no authored `a11YPauseAriaLabel`, so the control
   * name participates in the same localization pipeline as visible copy.
   */
  pauseButton: {
    label: "Pause button",
    obelixKey: "carousel-pause-button",
  },
};

export const OBELIX_FORM_KEYS = {
  submitErrorToast: {
    label: "We ran into an issue submitting your request. Please try again.",
    obelixKey: "form-submit-error-toast",
  },
  submitErrorDismiss: { label: "Dismiss", obelixKey: "form-submit-error-toast-dismiss" },
};

/**
 * Shared chrome strings for CTA fallback link text. Each entry is the
 * English fallback that `t(obelixKey, label)` returns when ALH has not
 * yet supplied a localized value (or before the runtime is resolved);
 * call sites use these as the `?? ...` branch in expressions like
 * `ctaLink.text ?? t(KEY.obelixKey, KEY.label)`.
 */
export const OBELIX_CTA_KEYS = {
  readMore: { label: "Read More", obelixKey: "cta-read-more" },
  learnMore: { label: "Learn more", obelixKey: "cta-learn-more" },
};

/**
 * Shared chrome strings for the blog surface (home + post pages). The
 * matching values already live in `client/src/locales/en/ads-common.json`;
 * blog consumers on the parallel branch reference these keys via
 * `t(KEY.obelixKey, KEY.label)`.
 */
export const OBELIX_BLOG_KEYS = {
  allBlogPosts: { label: "All Blog Posts", obelixKey: "blog-all-blog-posts" },
  featuredStory: { label: "Featured Story", obelixKey: "blog-featured-story" },
  moreBlogPosts: { label: "More Blog Posts", obelixKey: "blog-more-blog-posts" },
  readPost: { label: "Read Post", obelixKey: "blog-read-post" },
  keyTakeaways: { label: "Key Takeaways", obelixKey: "blog-key-takeaways" },
};

/**
 * Join-key registry for the nav chrome (Phase 4, D2). Keyed by every
 * `navConfig` node `id` — TypeScript rejects a config node without a registry
 * entry (and an entry that names no config node), so the two can never drift.
 * Display strings resolve `t(OBELIX_NAV_KEYS[id].obelixKey, label)` against
 * the `ads-nav` namespace.
 */
export const OBELIX_NAV_KEYS = {
  homepage: { label: "Homepage", obelixKey: "nav-home" },
  solutions: { label: "Solutions", obelixKey: "nav-solutions" },
  "ad-formats": { label: "Ad Formats", obelixKey: "nav-ad-formats" },
  targeting: { label: "Targeting", obelixKey: "nav-targeting" },
  "measurement-and-reporting": {
    label: "Measurement & Reporting",
    obelixKey: "nav-measurement-reporting",
  },
  insights: { label: "Insights", obelixKey: "nav-insights" },
  partnerships: { label: "Partnerships", obelixKey: "nav-partnerships" },
  "case-studies": { label: "Case Studies", obelixKey: "nav-case-studies" },
  blog: { label: "Blog", obelixKey: "nav-blog" },
  faqs: { label: "FAQs", obelixKey: "nav-faqs" },
  "get-connected": { label: "Get Connected", obelixKey: "nav-get-connected" },
} as const satisfies Record<NavNodeId, ObelixKeyEntry>;

/**
 * Join-key registry for the footer chrome, keyed by the `textKey` / `titleKey`
 * / singleton keys carried by `footerConfig` — TypeScript enforces exact
 * coverage on both sides.
 */
export const OBELIX_FOOTER_KEYS = {
  "footer-solutions-title": { label: "Solutions", obelixKey: "footer-solutions-title" },
  "footer-audience-insights-title": {
    label: "Audience Insights",
    obelixKey: "footer-audience-insights-title",
  },
  "footer-resources-title": { label: "Resources", obelixKey: "footer-resources-title" },
  "footer-bottom-title": {
    label: "Default Footer Bottom Link Group",
    obelixKey: "footer-bottom-title",
  },
  "footer-link-ad-formats": { label: "Ad Formats", obelixKey: "footer-link-ad-formats" },
  "footer-link-targeting": { label: "Targeting", obelixKey: "footer-link-targeting" },
  "footer-link-measurement-reporting": {
    label: "Measurement & Reporting",
    obelixKey: "footer-link-measurement-reporting",
  },
  "footer-link-insights": { label: "Insights", obelixKey: "footer-link-insights" },
  "footer-link-still-watching": {
    label: "Still Watching",
    obelixKey: "footer-link-still-watching",
  },
  "footer-link-get-started": { label: "Get Started", obelixKey: "footer-link-get-started" },
  "footer-link-faqs": { label: "FAQs", obelixKey: "footer-link-faqs" },
  "footer-link-creative-specs": {
    label: "Creative Specs",
    obelixKey: "footer-link-creative-specs",
  },
  "footer-link-setup-activation": {
    label: "Setup and Activation",
    obelixKey: "footer-link-setup-activation",
  },
  "footer-link-about-netflix": {
    label: "About Netflix",
    obelixKey: "footer-link-about-netflix",
  },
  "footer-link-terms": {
    label: "Terms of Use and Privacy Statement",
    obelixKey: "footer-link-terms",
  },
  "footer-link-impressum": {
    label: "Impressum",
    obelixKey: "footer-link-impressum",
  },
  "footer-link-methodology": {
    label: "Description of Methodology",
    obelixKey: "footer-link-methodology",
  },
  "footer-cookies": { label: "Cookie Preferences", obelixKey: "footer-cookies" },
  "footer-copyright": { label: "© 2026 Netflix", obelixKey: "footer-copyright" },
  "footer-easter-egg-text": {
    label: "Are you still watching?",
    obelixKey: "footer-easter-egg-text",
  },
  "footer-newsletter-form-label": {
    label: "Newsletter Signup",
    obelixKey: "footer-newsletter-form-label",
  },
  "footer-newsletter-email-field-label": {
    label: "Email",
    obelixKey: "footer-newsletter-email-field-label",
  },
  "footer-newsletter-cta": { label: "Sign Up", obelixKey: "footer-newsletter-cta" },
  "footer-submission-approved": {
    label: "Thank you!",
    obelixKey: "footer-submission-approved",
  },
  "footer-valid-email-note": {
    label: "Please enter a valid email address",
    obelixKey: "footer-valid-email-note",
  },
} as const satisfies Record<FooterTextKey, ObelixKeyEntry>;

/**
 * Join-key registry for the language switcher, keyed by every `localeConfig`
 * locale `id`. `localName` resolves via the `ads-common` namespace.
 */
export const OBELIX_LOCALE_KEYS = {
  "en-US": {
    localName: {
      label: "English (United States)",
      obelixKey: "locale-local-name-en",
    },
  },
  "de-DE": {
    localName: { label: "Deutsch (Deutschland)", obelixKey: "locale-local-name-de" },
  },
  "es-MX": {
    localName: { label: "Español (México)", obelixKey: "locale-local-name-es" },
  },
  "es-ES": {
    localName: {
      label: "Español (España)",
      obelixKey: "locale-local-name-es-es",
    },
  },
  "fr-FR": {
    localName: { label: "Français (France)", obelixKey: "locale-local-name-fr" },
  },
  "it-IT": {
    localName: { label: "Italiano (Italia)", obelixKey: "locale-local-name-it" },
  },
  "ja-JP": { localName: { label: "日本語 (日本)", obelixKey: "locale-local-name-ja" } },
  "ko-KR": {
    localName: { label: "한국어 (대한민국)", obelixKey: "locale-local-name-ko" },
  },
  "pt-BR": {
    localName: { label: "Português (Brasil)", obelixKey: "locale-local-name-pt" },
  },
} as const satisfies Record<LocaleId, { localName: ObelixKeyEntry }>;

